// pages/technicians/allocationPrintDocuments.js
//
// Which document an allocation prints, and the PRODUCT RETURN SHEET template.
//   - ALLOCATED (on issue) -> Product Allotment Sheet
//     (existing template in AllocationUnitWisePage.jsx, unchanged)
//   - RETURNED             -> Product Return Sheet (below)
//   - CANCELLED            -> nothing: a cancelled allocation is a voided
//     issue (stock released, no handover/return recorded), kept for audit only
// The backend has the final say: GET /alloc/:id/print/allotment refuses a
// returned allocation (409 ALLOCATION_RETURNED_USE_RETURN_SHEET) and
// refuses a cancelled one (409 ALLOCATION_CANCELLED), and
// GET /alloc/:id/print/return refuses one that is not returned
// (409 ALLOCATION_NOT_RETURNED), so a stale row on screen can never print
// the wrong document.

export const PRINT_KIND = Object.freeze({ ALLOTMENT: "allotment", RETURN: "return" });

export const isReturnedAllocation = (row) => String(row?.status || "").toUpperCase() === "RETURNED";

export const isActiveAllocation = (row) => String(row?.status || "").toUpperCase() === "ALLOCATED";

// null = no printable document (Cancelled).
export const printKindFor = (row) =>
  isReturnedAllocation(row) ? PRINT_KIND.RETURN : isActiveAllocation(row) ? PRINT_KIND.ALLOTMENT : null;

// Only allocations that are still on issue belong on an allotment sheet.
export const allotmentPrintableRows = (rows) => (rows || []).filter(isActiveAllocation);

/** GET /alloc/:id/print/{allotment|return} -> { document_type, title, allocation } */
export async function loadPrintDocument(api, allocationId, kind) {
  const { data } = await api.get(`/alloc/${allocationId}/print/${kind}`);
  return data;
}

const esc = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

// Same display format as the allotment sheet (en-IN, "04 Aug 2026").
export const formatPrintDate = (date) => {
  if (!date) return "-";
  const dt = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(dt.getTime())) return String(date);
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

// Same "processor / RAM / storage" line as the allotment sheet.
export const specSummary = (product) => {
  if (!product) return "-";
  const ram = product.ram_gb ? `${product.ram_gb}GB` : "-";
  const storage = product.ssd_gb ? `${product.ssd_gb}GB SSD` : product.hdd_gb ? `${product.hdd_gb}GB HDD` : "-";
  return `${product.processor || "-"} / ${ram} / ${storage}`;
};

function currentUserName() {
  try {
    const saved = localStorage.getItem("user");
    const user = saved ? JSON.parse(saved) : {};
    return user?.name || user?.username || user?.email || "Logged-in User";
  } catch (e) {
    return "Logged-in User";
  }
}

const NOT_RECORDED = "Not recorded";

/**
 * PRODUCT RETURN SHEET for one RETURNED allocation (data from
 * GET /alloc/:id/print/return merged over the list row).
 */
export function buildReturnSheetHTML(allocation, { unitName } = {}) {
  const a = allocation || {};
  const org = {
    name: a.company_name || "Company Name",
    address: a.company_address || "-",
    phone: a.company_phone || "-",
    email: a.company_email || "-",
  };
  const issuedTo = a.issued_to_name || a.user_name || "-";
  const returnedBy = a.returned_by_person_name || issuedTo;
  const receivedBy = a.received_by_name || NOT_RECORDED;
  const box = (label, value, full = false) =>
    `<div class="info-box${full ? " full" : ""}"><span>${esc(label)}</span><b>${esc(value)}</b></div>`;

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>Product Return Sheet</title>
<style>
  @page { size: A4 portrait; margin: 10mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; font-family: Arial, sans-serif; color: #111827;
    -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { font-size: 12px; background: #ffffff; }
  .print-box { width: 100%; border: 2px solid #111827; padding: 12px; }
  .head { border-bottom: 2px solid #111827; padding-bottom: 10px; margin-bottom: 10px; }
  .brand { font-size: 18px; font-weight: 900; line-height: 1.25; overflow-wrap: anywhere; }
  .muted { font-size: 11px; color: #374151; line-height: 1.4; overflow-wrap: anywhere; }
  .doc-row { display: flex; justify-content: space-between; gap: 12px; margin-top: 6px; font-size: 11px; }
  .title { text-align: center; font-size: 16px; font-weight: 900; letter-spacing: 1px;
    text-transform: uppercase; border: 1px solid #111827; padding: 8px; margin-bottom: 10px; }
  .status-pill { display: inline-block; border: 1px solid #111827; padding: 3px 8px; font-size: 10px; font-weight: 900; }
  .section { font-size: 11px; font-weight: 900; text-transform: uppercase; margin: 10px 0 6px; }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 7px; }
  .info-box { border: 1px solid #d1d5db; padding: 7px; min-height: 44px; overflow-wrap: anywhere; }
  .info-box.full { grid-column: 1 / -1; }
  .info-box span { display: block; font-size: 9px; text-transform: uppercase; color: #6b7280;
    font-weight: 900; letter-spacing: 0.5px; margin-bottom: 3px; }
  .info-box b { display: block; font-size: 11px; line-height: 1.35; font-weight: 800; }
  .sign-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 6px; margin-top: 12px;
    page-break-inside: avoid; break-inside: avoid; }
  .sign-card { border: 1px solid #111827; padding: 6px; min-height: 82px; }
  .sign-card b { font-size: 10px; display: block; }
  .label { font-size: 8px; color: #374151; margin-top: 1px; }
  .sign-line { border-top: 1px solid #111827; margin-top: 18px; }
  .date-line { font-size: 13px; font-weight: 900; margin-top: 4px; letter-spacing: 1px; }
  .foot { margin-top: 12px; border-top: 1px solid #111827; padding-top: 7px; font-size: 10px; line-height: 1.4; color: #374151; }
</style>
</head>
<body>
  <div class="print-box">
    <div class="head">
      <div class="brand">${esc(org.name)}</div>
      <div class="muted">${esc(org.address)}</div>
      <div class="muted">${esc(org.phone)} | ${esc(org.email)}</div>
      <div class="doc-row">
        <div>Printed: <b>${esc(formatPrintDate(new Date()))}</b></div>
        <div>Allocation Ref #: <b>${esc(a.id ?? "-")}</b></div>
      </div>
    </div>

    <div class="title">Product Return Sheet</div>

    <div class="section">Product <span class="status-pill">${esc(a.status || "RETURNED")}</span></div>
    <div class="info-grid">
      ${box("Product", a.product_name || "-")}
      ${box("Serial No", a.serial_no || "-")}
      ${box("Model", a.model_no || "-")}
      ${box("Specification", specSummary(a))}
      ${box("Unit", a.unit_name || unitName || "-")}
      ${box("Quantity", Number(a.qty ?? 0))}
    </div>

    <div class="section">Issue &amp; Return</div>
    <div class="info-grid">
      ${box("Originally Issued To", issuedTo)}
      ${box("Company", a.company_name || org.name || "-")}
      ${box("Original Issue Date", formatPrintDate(a.allocated_at))}
      ${box("Return Date", formatPrintDate(a.return_at))}
      ${box("Returned By", returnedBy)}
      ${box("Received By", receivedBy)}
      ${box("Condition at Return", a.return_condition || NOT_RECORDED)}
      ${box("Allocation / Reference ID", a.id ?? "-")}
      ${box("Return Remarks", a.return_remarks || NOT_RECORDED, true)}
    </div>

    <div class="sign-grid">
      <div class="sign-card">
        <div><b>Returned By</b></div>
        <div class="label">Name &amp; Signature</div>
        <div class="sign-line"></div>
        <div class="label">Date</div>
        <div class="date-line">____ / ____ / ______</div>
      </div>
      <div class="sign-card">
        <div><b>Received By</b></div>
        <div class="label">Name &amp; Signature</div>
        <div class="sign-line"></div>
        <div class="label">Date</div>
        <div class="date-line">____ / ____ / ______</div>
      </div>
      <div class="sign-card">
        <div><b>Approved By</b></div>
        <div class="label">Name &amp; Signature</div>
        <div class="sign-line"></div>
        <div class="label">Date</div>
        <div class="date-line">____ / ____ / ______</div>
      </div>
    </div>

    <div class="foot">
      <b>Note:</b> The above product has been returned and received back into inventory on the return date shown.
      Prepared by ${esc(currentUserName())}.
    </div>
  </div>

  <script>
    window.onload = function(){
      try { window.focus(); } catch(e) {}
      setTimeout(function(){ window.print(); }, 300);
    };
  </script>
</body>
</html>`;
}
